import { useCallback } from 'react';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useIsNativeEmbed } from '../../navigation/hooks/useIsNativeEmbed';
import { ReleaseFocus, releasePageDeepLink, releasePageParams } from './releasePageLink';

/** Sends the web view to a link. A seam for the tests; the page uses `window.location.assign`. */
type Navigate = (url: string) => void;

function navigateWindow(url: string): void {
  window.location.assign(url);
}

interface UseOpenReleasePageProps {
  /** The artist of the row. The native link carries it, so the app selects that artist first. */
  accountId: number | null;
  navigate?: Navigate;
}

/**
 * Opens the song page of a catalog row on the Assets tab.
 *
 * In a browser it goes to `promoteRelease?catalogItemUuid=<uuid>&tab=assets` (plus `focus` when
 * given). Inside the iOS and Android web view (`useIsNativeEmbed`) it sends the app the
 * `promote/promoteRelease` deep link, because the apps draw the song page natively. A row with no
 * uuid opens nothing: the song page finds its row by the uuid.
 */
export function useOpenReleasePage({ accountId, navigate = navigateWindow }: UseOpenReleasePageProps) {
  const router = useSMLRouter();
  const isNativeEmbed = useIsNativeEmbed();

  return useCallback(
    (catalogItemUuid: string, focus?: ReleaseFocus) => {
      if (catalogItemUuid === '') {
        return;
      }
      if (isNativeEmbed) {
        navigate(releasePageDeepLink(catalogItemUuid, accountId));
        return;
      }
      router.push(SMLRoutes.promoteRelease, releasePageParams(catalogItemUuid, focus));
    },
    [accountId, isNativeEmbed, navigate, router]
  );
}
